import i18n from 'src/i18n';
import { PlanBlock, PlanExercise } from 'src/models/workout-plan/_types';
import { formatSecondsToTime } from 'src/utils/dateFormatter';

const t = (key: string, options?: any) =>
  i18n.t(`tvDisplay.${key}`, options) as string;

export function isTruthyParam(value: string | null): boolean {
  if (!value) return false;
  return ['true', '1', 'yes'].includes(value.toLowerCase());
}

const convertor = {
  convertWeight(value, isMetric, useMetric, precision) {
    const accuracy = Math.pow(10, precision) || 1;
    if (isMetric && !useMetric) {
      return Math.round(value * 2.20462 * accuracy) / accuracy;
    }
    if (!isMetric && useMetric) {
      return Math.round(value * 0.453592 * accuracy) / accuracy;
    }
    return value;
  },
  convertHeight(value, isMetric, useMetric, precision) {
    const accuracy = Math.pow(10, precision) || 1;
    if (isMetric && !useMetric) {
      return Math.round(value * 0.393701 * accuracy) / accuracy;
    }
    if (!isMetric && useMetric) {
      return Math.round(value * 2.54 * accuracy) / accuracy;
    }
    return value;
  },
  convertMiles(value, isMetric, useMetric, precision) {
    const accuracy = Math.pow(10, precision) || 1;
    if (isMetric && !useMetric) {
      return Math.round(value * 0.621371 * accuracy) / accuracy;
    }
    if (!isMetric && useMetric) {
      return Math.round(value * 1.60934 * accuracy) / accuracy;
    }
    return value;
  },
  convertDistance(value, useMetric, precision, outputMeasurement, toFeet) {
    const accuracy = Math.pow(10, precision) || 1;

    if (toFeet) {
      if (useMetric) {
        if (outputMeasurement == 'miles' || outputMeasurement == 'mi') {
          return value * 3280.84;
        }
        if (outputMeasurement == 'yards' || outputMeasurement == 'feet') {
          return value * 3.28084;
        }
      } else {
        if (outputMeasurement == 'miles' || outputMeasurement == 'mi') {
          return value * 5280.0;
        }
        if (outputMeasurement == 'yards') {
          return value * 3.0;
        }
        if (outputMeasurement == 'feet') {
          return value;
        }
      }
    } else if (useMetric) {
      if (outputMeasurement == 'miles' || outputMeasurement == 'mi') {
        return Math.round((value / 3280.84) * accuracy) / accuracy;
      }
      if (outputMeasurement == 'yards' || outputMeasurement == 'feet') {
        return Math.round((value / 3.28084) * accuracy) / accuracy;
      }
    } else {
      if (outputMeasurement == 'miles' || outputMeasurement == 'mi') {
        return Math.round((value / 5280) * accuracy) / accuracy;
      }
      if (outputMeasurement == 'yards') {
        return Math.round((value / 3) * accuracy) / accuracy;
      }
      if (outputMeasurement == 'feet') {
        return Math.round(value * accuracy) / accuracy;
      }
    }
  },
};

export function ensureSortedBySortOrder<T extends { sortOrder?: number }>(
  items: T[]
): T[] {
  return [...items].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

export function pluralize(
  value: number | string,
  singular: string,
  plural: string
) {
  const num = Number(value);
  if (Number.isFinite(num) && num === 1) return singular;
  return plural;
}

export function repsToString(reps: number | string | null | undefined): string {
  if (reps === null || reps === undefined) return '';
  const raw = String(reps);
  if (!raw.trim()) return '';
  return raw.replace(/(^-1$)|(,-1)|(-1,)/g, (match) =>
    match.replace(/-1/, t('max'))
  );
}

export function convertMetric(value: any, type: any, usesMetric: any) {
  if (type === 'lbs') {
    if (value && value.toString().indexOf(',') > -1) {
      const weights = value.toString().split(',');
      return weights
        .map((w: any) => convertor.convertWeight(w, false, usesMetric, null))
        .join(', ');
    }
    return convertor.convertWeight(value, false, usesMetric, null);
  }
  if (
    type === 'mi' ||
    type === 'miles' ||
    type === 'yards' ||
    type === 'feet'
  ) {
    if (value && value.toString().indexOf(',') > -1) {
      const distances = value.toString().split(',');
      return distances
        .map((d: any) =>
          convertor.convertDistance(d, usesMetric, 2, type, false)
        )
        .join(', ');
    }
    return convertor.convertDistance(value, usesMetric, 2, type, false);
  }
  return null;
}

// seconds-to-time
function stt(value) {
  let time: string | null = null;

  if (value && value.toString().indexOf(',') > -1) {
    const times = value.toString().split(',');
    time = times.map((s) => formatSecondsToTime(Number(s))).join(', ');
  } else {
    time = formatSecondsToTime(Number(value));
  }

  return time;
}

/**
 * buildHelper(([exercise, alt, usesMetric, formatName])
 * @param ex
 * @param alt
 * @param usesMetric
 * @param formatName
 * @returns
 */
export function summaryString(
  ex: PlanExercise,
  alt: boolean,
  usesMetric: boolean,
  formatName?: boolean
): string {
  const stats: string[] = [];

  const hasSets =
    ex.sets &&
    (ex.sets > 0 ||
      ex.sets == -1 ||
      String(ex.sets).indexOf(',') !== -1 ||
      String(ex.sets).indexOf('-') !== -1);

  const hasReps = ex.fields?.includes('reps');

  const hasWeight = ex.fields?.includes('weight');

  const hasDistance = ex.fields?.includes('distance');

  const hasTime = ex.fields?.includes('time');

  const hasRest = ex.rest && ex.rest > 0;

  let prefix = '';
  let weight = '';
  let distance = '';
  if (ex.usesMetric) {
    weight = 'kg';
    distance = 'km';
  } else {
    weight = 'lbs';
    distance = 'mi';
  }
  weight = usesMetric ? 'kg' : 'lbs';
  if (
    ex.distanceMeasurement === 'miles' ||
    ex.distanceMeasurement === 'mi' ||
    ex.distanceMeasurement === 'mis'
  ) {
    distance = usesMetric ? 'km' : 'mi';
  }
  if (ex.distanceMeasurement === 'yards') {
    distance = usesMetric ? 'm' : 'yds';
  }
  if (ex.distanceMeasurement === 'feet') {
    distance = usesMetric ? 'm' : 'ft';
  }

  let distanceValue: number | string = 0;
  let weightValue: number | string = 0;
  if (alt) {
    if (hasDistance) {
      distanceValue = convertMetric(
        ex.distance,
        ex.distanceMeasurement,
        usesMetric
      );
    }
    if (hasWeight) {
      weightValue = convertMetric(ex.weight, 'lbs', usesMetric);
    }
  } else {
    distanceValue =
      getConvertedDistance(ex.distance, usesMetric, ex.distanceMeasurement) ||
      '';
    weightValue = getConvertedWeight(ex.weight, usesMetric) || '';
  }

  if (hasSets) {
    prefix = `${ex.sets} ${formatName ? `${pluralize(ex.sets!, t('set'), t('sets'))} /` : 'x'} `;
  }

  if (hasReps) {
    const repsString = repsToString(ex.reps);
    stats.push(
      `${repsString} ${pluralize(repsString, t('rep'), t('reps'))}`.trim()
    );
  }

  if (hasWeight && ((weightValue && formatName) || !formatName)) {
    stats.push(`${weightValue || '--'} ${weight}`);
  }

  if (hasDistance && ((distanceValue && formatName) || !formatName)) {
    stats.push(`${distanceValue || '--'} ${distance}`);
  }

  if (hasTime) {
    stats.push(`${stt(ex.time)}${formatName ? t('work') : ''}`);
  }
  if (hasRest) {
    stats.push(`${stt(ex.rest)} ${t('rest')}`);
  }

  const result = `${prefix}${stats.join(formatName ? ' / ' : ', ')}`.trim();
  return result;
}

export function blockTextSummary(block: PlanBlock): string {
  const name = block.name || '';
  const rounds = block.rounds ? Number(block.rounds) : 0;
  const time = block.time ? Number(block.time) : 0;

  const hasRounds = Number.isFinite(rounds) && rounds > 0;
  const hasTime = Number.isFinite(time) && time > 0;

  let summary = name ? name + (hasRounds || hasTime ? ' - ' : '') : '';

  if (hasRounds) {
    summary += hasTime
      ? t('roundsFor', {
          rounds,
          time: stt(time),
        })
      : `${rounds} ${t('rounds')}`;
  } else if (hasTime) {
    summary += t('maxFor', { time: stt(time) });
  }

  return summary;
}

/**
 * Converts distance values using the same logic as the original Ember.js model
 * Handles both single values and comma-separated lists
 */
export function getConvertedDistance(
  distance: string | number | null | undefined,
  useMetric: boolean,
  distanceMeasurement?: string | null
): string | null {
  if (!distance) {
    return null;
  }

  if (distance.toString().indexOf(',') > -1) {
    const distances = distance.toString().split(',');
    return distances
      .map((d) =>
        convertor.convertDistance(d, useMetric, 2, distanceMeasurement, false)
      )
      .join(',');
  }

  return convertor.convertDistance(
    distance,
    useMetric,
    2,
    distanceMeasurement,
    false
  );
}

/**
 * Converts weight values using the same logic as the original Ember.js model
 * Handles both single values and comma-separated lists
 */
export function getConvertedWeight(
  weight: string | number | null | undefined,
  useMetric: boolean
): string | null {
  if (!weight) {
    return null;
  }

  if (weight.toString().indexOf(',') > -1) {
    const weights = weight.toString().split(',');
    return weights
      .map((w) => convertor.convertWeight(w, false, useMetric, null))
      .join(',');
  }

  return convertor.convertWeight(weight, false, useMetric, null);
}
